import type { IconProps } from "@/v2-ui/icon"
import Badge, {
  type BadgeVariant,
} from "@/v2-backoffice-client/components/badge"
import {
  describeUserLicenseSeat,
  USER_LICENSE_SEAT_STANDING_LABEL,
  type UserLicenseSeat,
  type UserLicenseSeatStanding,
} from "@/v2-backoffice-client/license/seats/license.seats.utils"

export type UserSeatBadgesProps = {
  seats: readonly UserLicenseSeat[]
  /** The person, as the hovers name them. */
  userName: string
  /** The customer whose licenses these are. */
  customerName: string
}

const STANDING_VISUAL: Record<
  UserLicenseSeatStanding,
  { variant: BadgeVariant; iconSrc: IconProps["src"] }
> = {
  held: { variant: "positive", iconSrc: "user-check" },
  missing: { variant: "outline", iconSrc: "user-xmark" },
  everyone: { variant: "neutral", iconSrc: "users" },
}

/**
 * One pill per seated license of the person's customer, saying where they
 * stand on it: green with a tick for a seat they hold, hollow with a cross
 * for a limited license they are not named on (it does not reach them),
 * grey for a license that reaches everyone at the customer. The hover says
 * why and where it is changed.
 */
function UserSeatBadges(props: UserSeatBadgesProps) {
  const { seats, userName, customerName } = props

  return (
    <div className="flex flex-wrap gap-1">
      {seats.map((seat) => {
        const visual = STANDING_VISUAL[seat.standing]
        return (
          <Badge
            key={seat.LicenseId}
            variant={visual.variant}
            iconSrc={visual.iconSrc}
            tooltip={describeUserLicenseSeat(seat, userName, customerName)}
          >
            {seat.ProductName}
            <span className="opacity-70">
              {" "}
              · {USER_LICENSE_SEAT_STANDING_LABEL[seat.standing]}
            </span>
          </Badge>
        )
      })}
    </div>
  )
}

export default UserSeatBadges
